import { useEffect, useState } from 'react';

/**
 * How long a caller waits for LaunchDarkly before trusting the value the SDK is
 * already serving.
 *
 * Neither SDK exposes a readiness signal that is both reliable and free of an
 * extra flag exposure, so the wait is bounded instead of open-ended: a surface
 * whose flag-off state is terminal gets a moment to avoid showing it on a cold
 * start, and can never be stranded waiting for an answer that never comes.
 */
const FLAG_SETTLE_TIMEOUT_MS = 2000;

/**
 * Latches once LaunchDarkly answers, or once the bounded wait expires. Never
 * returns to pending: a flag set can empty again while the client swaps
 * context, and a caller that treats that as pending would never recover.
 */
export const useSettledFlagState = (
  isResolved: boolean,
  timeoutMs = FLAG_SETTLE_TIMEOUT_MS,
): boolean => {
  const [hasSettled, setHasSettled] = useState(false);

  useEffect(() => {
    if (isResolved) {
      setHasSettled(true);
      return;
    }

    const timeout = setTimeout(() => setHasSettled(true), timeoutMs);

    return () => clearTimeout(timeout);
  }, [isResolved, timeoutMs]);

  return isResolved || hasSettled;
};
